import { useEffect, useRef, useState } from "react";
import {
  X,
  Lock,
  CircleNotch,
  Check,
  ShieldCheck,
  CreditCard,
  CheckCircle,
} from "@phosphor-icons/react";

/**
 * Peach Payments V2 Embedded Checkout — Decal Labs premium build.
 *
 * Three CX upgrades vs. the previous version:
 *   1. Order Summary card (top): plan name, feature bullets, promo line,
 *      total in big display font. Buyer can verify what they're paying
 *      for without trusting the iframe alone.
 *   2. Trust strip (bottom of summary): Visa / MC / Amex / Apple Pay
 *      marks, lock icon, "256-bit SSL", "PCI DSS Level 1", Peach badge.
 *   3. Branded skeleton loader replaces the bare spinner (zero layout
 *      jump when the iframe finally mounts).
 *   4. Success transition: animated checkmark + brief "Payment confirmed"
 *      moment before the parent navigates away — feels finished, not
 *      abrupt.
 *
 * Loads `checkout.js` from the sandbox or live CDN, then calls
 * `window.Checkout.initiate({ key: entityId, checkoutId })` and renders
 * the payment form inside `#peach-payment-form`.
 *
 * Lifecycle:
 *  - onCompleted  → fire `onCompleted` callback (parent navigates to
 *                   /checkout/callback?sub_id=...&peach=1 so it can poll
 *                   webhook-driven status).
 *  - onCancelled  → close the modal, no state change locally.
 *  - onExpired    → close the modal with a toast.
 */
let _peachScriptPromise = null;

function loadPeachScript(scriptUrl) {
  if (_peachScriptPromise) return _peachScriptPromise;
  _peachScriptPromise = new Promise((resolve, reject) => {
    if (window.Checkout) return resolve();
    const existing = document.getElementById("peach-checkout-js");
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("script load failed")));
      return;
    }
    const s = document.createElement("script");
    s.id = "peach-checkout-js";
    s.src = scriptUrl;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      _peachScriptPromise = null;
      reject(new Error("Failed to load Peach checkout.js"));
    };
    document.body.appendChild(s);
  });
  return _peachScriptPromise;
}

export default function PeachCheckoutModal({
  open,
  embed, // { checkout_id, entity_id, env, script_url }
  amountLabel, // e.g. "R59.99 / month"
  planLabel, // e.g. "Premium plan"
  features = [], // [str, str, str] — order summary bullets
  promo = null, // { code, originalLabel, finalLabel, discountLabel } | null
  onCompleted,
  onCancelled,
  onExpired,
  onClose,
}) {
  const containerRef = useRef(null);
  const instanceRef = useRef(null);
  const [stage, setStage] = useState("loading"); // loading | ready | success | error
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !embed?.checkout_id) return;
    let cancelled = false;
    setStage("loading");
    setError(null);

    (async () => {
      try {
        await loadPeachScript(embed.script_url);
        if (cancelled) return;
        if (!window.Checkout) throw new Error("Checkout SDK not available");
        // Re-init if there's a leftover instance.
        if (instanceRef.current) {
          try {
            instanceRef.current.destroy?.();
          } catch (_) { /* noop */ }
          instanceRef.current = null;
        }
        const inst = window.Checkout.initiate({
          key: embed.entity_id,
          checkoutId: embed.checkout_id,
          eventHandlers: {
            onCompleted: (ev) => {
              // Brief "Payment confirmed" overlay before the parent
              // navigates away. Feels finished, not abrupt.
              setStage("success");
              setTimeout(() => {
                try { onCompleted?.(ev); } catch (_) { /* noop */ }
              }, 950);
            },
            onCancelled: (ev) => {
              try { onCancelled?.(ev); } catch (_) { /* noop */ }
            },
            onExpired: (ev) => {
              try { onExpired?.(ev); } catch (_) { /* noop */ }
            },
          },
          // Modern customisations API — same compactness wins as before
          // (hide duplicate cancel + amount inside the iframe so our
          // bespoke order summary above takes the lead).
          customisations: {
            showCancelButton: false,
            showAmountField: false,
            theme: {
              brand: { primary: "#FF3300" }, // Decal Labs accent
            },
            card: {
              submitButtonText: amountLabel ? `Pay ${amountLabel}` : "Pay now",
              showBillingFields: true,
            },
          },
        });
        if (cancelled) return;
        inst.render("#peach-payment-form");
        instanceRef.current = inst;
        setStage("ready");
      } catch (e) {
        if (cancelled) return;
        console.error("Peach widget error:", e);
        setError(e.message || "Failed to load secure checkout");
        setStage("error");
      }
    })();

    return () => {
      cancelled = true;
      if (instanceRef.current) {
        try { instanceRef.current.destroy?.(); } catch (_) { /* noop */ }
        instanceRef.current = null;
      }
    };
  }, [open, embed?.checkout_id, embed?.entity_id, embed?.script_url]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="peach-checkout-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative bg-white border border-[var(--border)] w-full max-w-[640px] max-h-[94vh] overflow-y-auto shadow-2xl">
        {/* ============ Header ============ */}
        <div className="relative bg-black text-white px-6 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-9 w-9 bg-[var(--accent)] flex items-center justify-center shrink-0">
                <Lock size={16} weight="bold" />
              </div>
              <div className="min-w-0">
                <div className="micro-label text-white/60 leading-none">
                  Secure checkout
                </div>
                <div className="font-display font-black text-base mt-1 leading-none truncate">
                  DECAL LABS
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded transition-colors"
              data-testid="peach-modal-close"
              aria-label="Close"
            >
              <X size={18} weight="bold" />
            </button>
          </div>
        </div>

        {/* ============ Order Summary Card ============ */}
        <div className="px-6 pt-6">
          <div className="border border-[var(--border)] bg-[var(--bg-2)] p-5">
            <div className="micro-label text-[var(--fg-2)] mb-3">
              Order Summary
            </div>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="font-display font-black text-xl leading-tight">
                  {planLabel || "Subscription"}
                </div>
                {features.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {features.slice(0, 3).map((f, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-xs text-[var(--fg)]"
                      >
                        <Check
                          size={12}
                          weight="bold"
                          className="text-[var(--accent)] mt-0.5 shrink-0"
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="text-right shrink-0">
                {promo ? (
                  <>
                    <div className="font-mono text-xs text-[var(--fg-2)] line-through">
                      {promo.originalLabel}
                    </div>
                    <div className="font-display font-black text-2xl text-[var(--accent)] leading-none mt-1">
                      {promo.finalLabel || amountLabel}
                    </div>
                  </>
                ) : (
                  <div className="font-display font-black text-2xl leading-none">
                    {amountLabel}
                  </div>
                )}
              </div>
            </div>

            {promo && (
              <div
                className="mt-4 px-3 py-2 bg-white border border-[var(--accent)] flex items-center justify-between gap-2"
                data-testid="peach-modal-promo"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--accent)] font-bold">
                    PROMO
                  </span>
                  <span className="font-mono text-xs truncate">{promo.code}</span>
                </div>
                <span className="font-mono text-xs font-bold text-[var(--accent)] shrink-0">
                  −{promo.discountLabel || "Discount applied"}
                </span>
              </div>
            )}
          </div>

          {/* ============ Trust strip ============ */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 px-1">
            <div
              className="flex items-center gap-2.5"
              data-testid="peach-trust-badges"
            >
              <PaymentMark label="VISA" />
              <PaymentMark label="MC" />
              <PaymentMark label="AMEX" />
              <PaymentMark label="DCB" />
            </div>
            <div className="flex items-center gap-3 text-[10px] font-mono tracking-widest uppercase text-[var(--fg-2)]">
              <span className="flex items-center gap-1">
                <ShieldCheck size={11} weight="bold" /> 256-bit SSL
              </span>
              <span className="hidden sm:flex items-center gap-1">
                <CreditCard size={11} weight="bold" /> PCI DSS
              </span>
            </div>
          </div>
        </div>

        {/* ============ Payment iframe / states ============ */}
        <div className="px-6 py-5">
          {stage === "loading" && <SkeletonCheckout />}
          {stage === "success" && (
            <div
              className="py-16 text-center animate-in"
              data-testid="peach-success"
            >
              <div className="inline-flex h-16 w-16 items-center justify-center bg-[var(--success,#059669)] text-white rounded-full mb-4 animate-in zoom-in duration-300">
                <CheckCircle size={36} weight="fill" />
              </div>
              <div className="font-display font-black text-2xl">
                Payment confirmed
              </div>
              <p className="mt-2 font-mono text-xs tracking-widest uppercase text-[var(--fg-2)]">
                Activating your subscription…
              </p>
            </div>
          )}
          {stage === "error" && (
            <div className="py-12 text-center" data-testid="peach-error">
              <p className="text-red-600 font-medium">{error}</p>
              <button
                type="button"
                onClick={onClose}
                className="mt-4 px-4 py-2 border border-[var(--border)] hover:bg-[var(--bg-2)] font-mono text-sm uppercase tracking-wider"
              >
                Close
              </button>
            </div>
          )}
          {/* Always render the iframe slot so Peach can mount into it
              even while the skeleton sits on top. When `stage === ready`
              the skeleton is unmounted and the iframe takes over. */}
          <div
            id="peach-payment-form"
            ref={containerRef}
            style={{
              minHeight: stage === "ready" ? "320px" : "0",
              display: stage === "ready" ? "block" : "none",
            }}
            className={
              stage === "ready"
                ? "[&_iframe]:!w-full [&_iframe]:!max-w-full [&_iframe]:!border-0 [&_iframe]:!h-auto"
                : ""
            }
            data-testid="peach-payment-form"
          />
        </div>

        {/* ============ Footer ============ */}
        <div className="px-6 py-4 border-t border-[var(--border)] bg-[var(--bg-2)]">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] text-[var(--fg-2)] flex items-center gap-1.5 min-w-0">
              <Lock size={11} weight="bold" className="shrink-0" />
              <span className="truncate">
                Secured by Peach Payments. We never see your card details.
              </span>
            </p>
            <span className="text-[10px] font-mono tracking-widest uppercase text-[var(--fg-2)] shrink-0">
              {embed?.env === "live" ? "LIVE" : "TEST"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----- Sub-components -----

/**
 * Pill-style payment-method mark. We render label text (e.g. "VISA")
 * inside a brutalist border instead of full-color logos — keeps the
 * footprint tiny, avoids licensing issues with vendor SVGs, and matches
 * the Decal Labs brutalist aesthetic.
 */
function PaymentMark({ label }) {
  return (
    <div
      className="inline-flex items-center justify-center h-6 px-2 border border-[var(--border)] bg-white font-mono text-[10px] font-bold tracking-wider text-[var(--fg)]"
      aria-label={label}
    >
      {label}
    </div>
  );
}

/**
 * Brand-aligned skeleton for the checkout iframe area. The shapes
 * roughly mirror Peach's layout (wallets row → method tabs → card
 * fields → submit button) so there's zero perceived layout jump when
 * the real iframe takes over.
 */
function SkeletonCheckout() {
  return (
    <div className="space-y-3 animate-pulse" data-testid="peach-skeleton">
      {/* wallets row */}
      <div className="flex gap-2">
        <div className="h-10 flex-1 bg-[var(--bg-2)] border border-[var(--border)]" />
        <div className="h-10 flex-1 bg-[var(--bg-2)] border border-[var(--border)]" />
      </div>
      {/* method tabs */}
      <div className="flex gap-2">
        <div className="h-8 w-20 bg-[var(--bg-2)] border border-[var(--border)]" />
        <div className="h-8 w-20 bg-[var(--bg-2)] border border-[var(--border)]" />
        <div className="h-8 w-20 bg-[var(--bg-2)] border border-[var(--border)]" />
      </div>
      {/* card field */}
      <div className="h-11 bg-[var(--bg-2)] border border-[var(--border)]" />
      {/* exp + cvv row */}
      <div className="flex gap-2">
        <div className="h-11 flex-1 bg-[var(--bg-2)] border border-[var(--border)]" />
        <div className="h-11 w-24 bg-[var(--bg-2)] border border-[var(--border)]" />
      </div>
      {/* submit button */}
      <div className="h-12 bg-[var(--bg-2)] border border-[var(--border)]" />
      <p className="text-center mt-4 font-mono text-[10px] tracking-widest uppercase text-[var(--fg-2)]">
        <CircleNotch
          size={12}
          weight="bold"
          className="inline animate-spin mr-2"
        />
        Loading secure form
      </p>
    </div>
  );
}
